import type { HTTPMethod } from '@warp-drive/core-types/request'
import { timeout, task } from 'ember-concurrency'
import type GravityAdapterV3 from 'teacher-ui-client/adapters/gravity/v3-adapter'

const MAX_QUEUE_SIZE = 20

interface BulkQueueItem {
  record: BulkItem
  resolver: {
    resolve: (value?: { data: BulkItem }) => void
    reject: (value: any) => void
  }
}

type BulkItem = Record<any, any>
type AdditionalBulkData = Record<string, unknown>
/**
 * A small class to call an action which collects successive calls and sends them as collection
 * to the endpoint.
 *
 * @Class BulkManager
 */
export default class BulkManager {
  _bulkQueue: BulkQueueItem[] = []

  #url: string

  _ajaxService: GravityAdapterV3['ajax']

  timeOut: number

  /**
   * Constructor to set defaults
   * @param {Object} ajaxService - It is very important to bind the adapter scope to this service with `.bind(adapter)`
   * @param {String} apiUrl - 'the url for the bulk action'
   */
  constructor(ajaxService: GravityAdapterV3['ajax'], apiUrl: string, { timeOut = 50 } = {}) {
    this.#url = apiUrl
    this._ajaxService = ajaxService
    this.timeOut = timeOut
  }

  /**
   * Adds a JSONAPI object to the queue, alongside associated resolve and reject functions. It calls a debounced task,
   * which will send that request after the event storm.
   *
   * The maxConcurrency for this task acts as a maximum queue size, allowing us to batch the bulk requests (say, if
   * there's 100 create requests to make, we can do 5 requests of size 20 each. Each request waiting for the one before
   * it to complete)
   *
   * The idea is that either the timeout in the debounced task is reached and the request goes out, OR we reach the
   * max batch size here, so no more records are added to the batch queue attempts, no more attempts are made to perform
   * the debounce task, the timeout is reached and the request goes out.
   *
   * When the request returns, those records will be have their resolve functions called, the tasks complete, the
   * concurrency of this task is freed up, and we add more items to the bulk queue...
   *
   * @param method - Possible vlues are DELETE|PUT|...
   * @param record - a JSONAPI object to add to the queue
   * @param additionalBulkData - optional, use if there is extra data needed for the request. Last one wins.
   */
  callBulkAction = task(
    { enqueue: true, maxConcurrency: MAX_QUEUE_SIZE },
    async (method: HTTPMethod, record: BulkItem, additionalBulkData?: AdditionalBulkData) => {
      const bulkPromise = new Promise((resolve, reject) => {
        const resolver = { resolve, reject }
        this._bulkQueue = [...this._bulkQueue, { record, resolver }]
      })

      void this._bulkAction.perform(method, additionalBulkData)

      /* Task function is required to be async, else it will not work. You cannot just return the promise here and
       * remove the async from the function, like you can with regular functions.
       */
      return await bulkPromise
    },
  )

  /**
   * Task which executes an ajax request for a given method, emptying the current bulk queue. It's debounced by the
   * timeout given in the constructor.
   * @param method - Possible values are DELETE|PUT|...
   * @param additionalBulkData - optional, use if there is extra data needed for the request. Last one wins.
   */
  _bulkAction = task({ restartable: true }, async (method: HTTPMethod, additionalBulkData?: AdditionalBulkData) => {
    await timeout(this.timeOut)

    const bulkData = this._bulkQueue.map((queueItem) => queueItem.record)
    const bulkResolvers = this._bulkQueue.map((queueItem) => queueItem.resolver)

    this._bulkQueue = []

    const options = {
      data: {
        data: bulkData,
        ...additionalBulkData,
      },
    }

    this._ajaxService(this.#url, method, options)
      .then((response) => {
        // sometimes response can be empty/null (no response.data) - ie for PATCH and DELETE's
        const data = response && 'data' in response ? response.data : []
        if (Array.isArray(data) && data.length > 0) {
          const d = data as BulkItem[]
          /* for bulk create to work we rely on the order of the array of resources - response should contain data items
           * in the same order that they were in the request.
           */
          return bulkResolvers.forEach((p, index) => {
            p.resolve({ data: d[index] })
          })
        }

        return bulkResolvers.forEach((p) => p.resolve())
      })
      // disable rule here, promise.reject() is not an array prototype extension
      // eslint-disable-next-line ember/no-array-prototype-extensions
      .catch((e) => bulkResolvers.forEach((p) => p.reject(e)))
  })
}
